import {
  Location01Icon,
  Navigation04Icon,
  ParkingAreaSquareIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react-native';
import { useGetSessionLocation } from '@sunrise-ui/api-client';
import type { WithResponsive } from '@sunrise-ui/primitives';
import {
  Button,
  ButtonVariants,
  Colors,
  Loader,
  Modal,
  TextBold,
  TextReg,
  useDynamicStyles,
} from '@sunrise-ui/primitives';
import React from 'react';
import { Linking, Platform, ScrollView, StyleSheet, View } from 'react-native';

type Props = {
  locationId?: string;
  visible: boolean;
  onClose: () => void;
};

export function GettingThereModal({ locationId, visible, onClose }: Props) {
  const styles = useDynamicStyles(createStyles);
  const { data: location, isLoading } = useGetSessionLocation(locationId);

  const handleOpenMap = async () => {
    if (!location?.address) return;

    const fullAddress = [
      location.name,
      location.address,
      location.city,
      location.state,
      location.postal_code,
    ]
      .filter(Boolean)
      .join(', ');

    const encodedAddress = encodeURIComponent(fullAddress);

    try {
      let mapUrl: string;

      if (Platform.OS === 'ios') {
        // Try Apple Maps first on iOS
        mapUrl = `maps://?q=${encodedAddress}`;
        const canOpenAppleMaps = await Linking.canOpenURL(mapUrl);

        if (!canOpenAppleMaps) {
          // Fallback to Google Maps
          mapUrl = `https://maps.google.com/?q=${encodedAddress}`;
        }
      } else {
        // Use Google Maps on Android and web
        mapUrl = `https://maps.google.com/?q=${encodedAddress}`;
      }

      await Linking.openURL(mapUrl);
    } catch (error) {
      console.error('Failed to open map:', error);
    }
  };

  const formatAddress = () => {
    if (!location) return '';

    const cityStateZip = [
      [location.city, location.state].filter(Boolean).join(', '),
      location.postal_code,
    ]
      .filter(Boolean)
      .join(' ');

    const parts = [location.address, cityStateZip].filter(Boolean);

    return parts.join('\n');
  };

  return (
    <Modal
      visible={visible}
      hide={onClose}
      title="Getting There"
      size="medium"
      contentStyle={styles.modalContent}
    >
      <View style={styles.container}>
        {isLoading ? (
          <Loader style={styles.loader} />
        ) : (
          <ScrollView contentContainerStyle={styles.scrollContent}>
            {/* Location Section */}
            {location && (
              <View style={styles.section}>
                <View style={styles.sectionHeader}>
                  <HugeiconsIcon
                    icon={Location01Icon}
                    size={20}
                    color={Colors.text}
                  />
                  <TextBold style={styles.sectionTitle}>Location</TextBold>
                </View>

                <View style={styles.sectionContent}>
                  {location.name && (
                    <TextBold style={styles.locationName}>
                      {location.name}
                    </TextBold>
                  )}
                  <TextReg style={styles.address}>{formatAddress()}</TextReg>

                  <View style={styles.buttonContainer}>
                    <Button
                      variant={ButtonVariants.PINK}
                      size="medium"
                      onPress={handleOpenMap}
                      icon={() => (
                        <HugeiconsIcon
                          icon={Navigation04Icon}
                          size={16}
                          color={Colors.tint}
                        />
                      )}
                    >
                      Open in Maps
                    </Button>
                  </View>
                </View>
              </View>
            )}

            {/* Parking Section */}
            {location?.parking_instructions && (
              <View style={styles.section}>
                <View style={styles.sectionHeader}>
                  <HugeiconsIcon
                    icon={ParkingAreaSquareIcon}
                    size={20}
                    color={Colors.text}
                  />
                  <TextBold style={styles.sectionTitle}>Parking</TextBold>
                </View>

                <View style={styles.sectionContent}>
                  <TextReg style={styles.instructionsText}>
                    {location.parking_instructions}
                  </TextReg>
                </View>
              </View>
            )}

            {/* Wayfinding Section */}
            {location?.wayfinding_instructions && (
              <View style={styles.section}>
                <View style={styles.sectionHeader}>
                  <HugeiconsIcon
                    icon={Navigation04Icon}
                    size={20}
                    color={Colors.text}
                  />
                  <TextBold style={styles.sectionTitle}>Wayfinding</TextBold>
                </View>

                <View style={styles.sectionContent}>
                  <TextReg style={styles.instructionsText}>
                    {location.wayfinding_instructions}
                  </TextReg>
                </View>
              </View>
            )}
          </ScrollView>
        )}
      </View>
    </Modal>
  );
}

const createStyles = ({ isMobile }: WithResponsive) =>
  StyleSheet.create({
    container: {
      flex: 1,
      width: '100%',
    },
    modalContent: {
      padding: 0,
      paddingTop: isMobile ? 8 : 24,
      paddingBottom: isMobile ? 24 : 32,
    },
    scrollContent: {
      gap: 24,
      marginHorizontal: isMobile ? 16 : 32,
    },
    title: {
      fontSize: 24,
      textAlign: 'center',
      marginBottom: 8,
    },
    section: {
      gap: 16,
    },
    sectionHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    sectionTitle: {
      fontSize: 18,
      color: Colors.text,
    },
    sectionContent: {
      paddingLeft: 28, // Align with icon + gap
      gap: 12,
    },
    locationName: {
      fontSize: 16,
      color: Colors.text,
    },
    address: {
      fontSize: 14,
      color: Colors.text,
      opacity: 0.8,
      lineHeight: 20,
    },
    buttonContainer: {
      alignSelf: 'flex-start',
    },
    instructionsText: {
      fontSize: 14,
      color: Colors.text,
      opacity: 0.8,
      lineHeight: 20,
    },
    loader: {
      position: 'absolute',
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
      backgroundColor: Colors.purple0,
    },
  });
